import React from 'react';
import { Box, Checkbox, TextLink, Typography } from '@drone-js/core';
import { SubcategoryFilterProps, SubcategoryValueFilter } from 'component/page/search/SortAndFilter/CategoryFilter/CategoryFilter.types';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesSubcategoriesFilterClickEventActivitiesFieldsV2,
    ActivitiesSubcategoriesFilterSeenEventActivitiesFieldsV2,
    ActivitiesSubcategoriesSeeMoreButtonClickEventActivitiesFieldsV2,
    ActivitiesSubcategoriesSeeMoreButtonSeenEventActivitiesFieldsV2,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesSubcategoriesFilterClickEventActivitiesManifestV2,
    ActivitiesSubcategoriesFilterSeenEventActivitiesManifestV2,
    ActivitiesSubcategoriesSeeMoreButtonClickEventActivitiesManifestV2,
    ActivitiesSubcategoriesSeeMoreButtonSeenEventActivitiesManifestV2,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { SubcategoryId, SubcategoryParentId } from 'core/type/Subcategory.types';
import { useInView } from 'react-intersection-observer';
import { SortAndFilterContentVariant } from 'component/page/search/SortAndFilter/SortAndFilterContent.selectors';

const MAX_SUBCATEGORIES = 5;

const SubcategoryFilterItem: React.FC<{
    item: SubcategoryValueFilter;
    index: number;
    onSubcategoriesChange: (e: React.ChangeEvent<HTMLInputElement>, value: SubcategoryId) => void;
    variant: SortAndFilterContentVariant;
}> = (props) => {
    const { item, index, onSubcategoriesChange, variant } = props;
    const isSidebar = variant === SortAndFilterContentVariant.Sidebar;
    const isFilterBottomSheet = variant === SortAndFilterContentVariant.CategoryBottomSheet;

    const onSeen = (inView: boolean) => {
        if (inView) {
            trackElementEvent<ActivitiesSubcategoriesFilterSeenEventActivitiesFieldsV2>(ActivitiesSubcategoriesFilterSeenEventActivitiesManifestV2.ids, {
                action_element_index: index + 1,
                action_element_value: item.id,
                activities_categories: SubcategoryParentId[item.id],
                is_sidebar: isSidebar,
                is_filter_bottom_sheet: isFilterBottomSheet,
            });
        }
    };

    const { ref } = useInView({
        threshold: 0.75,
        triggerOnce: true,
        onChange: onSeen,
    });

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        trackElementEvent<ActivitiesSubcategoriesFilterClickEventActivitiesFieldsV2>(ActivitiesSubcategoriesFilterClickEventActivitiesManifestV2.ids, {
            action_element_index: index + 1,
            action_element_value: item.id,
            activities_categories: SubcategoryParentId[item.id],
            is_sidebar: isSidebar,
            is_filter_bottom_sheet: isFilterBottomSheet,
        });
        onSubcategoriesChange(e, item.id);
    };

    return (
        <Box
            key={item.label}
            data-element-name="activities-subcategories-filter"
            data-testid="activities-subcategories-filter"
            data-element-index={index + 1}
            flexDirection="row"
            display="flex"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            as="button"
            aria-label={item.label}
            p="12"
            ps="24"
            ref={ref}
        >
            <Checkbox key={item.label} selection={item.showAsSelected ? 'selected' : 'unselected'} onChange={onChange}>
                <Box display="flex" alignItems="flex-start" flexDirection="column">
                    <Typography variant="body-default" textColor="base.default" textAlign="start">
                        {item.label}
                    </Typography>
                </Box>
            </Checkbox>
        </Box>
    );
};
SubcategoryFilterItem.displayName = 'SubcategoryFilterItem';

const SubcategoryFilterItems: React.FC<SubcategoryFilterProps> = (props) => {
    const { items, onSubcategoriesChange, cms, variant, categoryId, index: categoryIndex } = props;
    const [isShowAll, setIsShowAll] = React.useState(false);
    const isSidebar = variant === SortAndFilterContentVariant.Sidebar;
    const isFilterBottomSheet = variant === SortAndFilterContentVariant.CategoryBottomSheet;

    const onSeen = (inView: boolean) => {
        if (inView) {
            trackElementEvent<ActivitiesSubcategoriesSeeMoreButtonSeenEventActivitiesFieldsV2>(ActivitiesSubcategoriesSeeMoreButtonSeenEventActivitiesManifestV2.ids, {
                action_element_index: categoryIndex + 1,
                action_element_value: isShowAll ? 'show less' : 'show more',
                is_sidebar: isSidebar,
                is_filter_bottom_sheet: isFilterBottomSheet,
                activities_categories: categoryId,
            });
        }
    };

    const { ref } = useInView({
        threshold: 0.75,
        triggerOnce: true,
        onChange: onSeen,
    });

    if (!items) {
        return null;
    }

    const onClickShowAll = () => {
        trackElementEvent<ActivitiesSubcategoriesSeeMoreButtonClickEventActivitiesFieldsV2>(ActivitiesSubcategoriesSeeMoreButtonClickEventActivitiesManifestV2.ids, {
            action_element_index: categoryIndex + 1,
            action_element_value: isShowAll ? 'show less' : 'show more',
            is_sidebar: isSidebar,
            is_filter_bottom_sheet: isFilterBottomSheet,
            activities_categories: categoryId,
        });
        setIsShowAll(!isShowAll);
    };

    const sortedItemsByCount = items.sort((a, b) => b.count - a.count);
    const visibleSubcategories = sortedItemsByCount.slice(0, MAX_SUBCATEGORIES);
    const displayedSubcategories = isShowAll ? sortedItemsByCount : visibleSubcategories;
    const shouldTruncate = visibleSubcategories.length !== sortedItemsByCount.length;

    return (
        <>
            {displayedSubcategories.map((item, index) => (
                <SubcategoryFilterItem key={item.id} item={item} index={index} onSubcategoriesChange={onSubcategoriesChange} variant={variant} />
            ))}
            <Box textAlign="end" pt="8">
                {shouldTruncate && (
                    <TextLink
                        ref={ref}
                        data-element-name="activities-subcategories-see-more-button"
                        data-testid="activities-subcategories-see-more-button"
                        label={isShowAll ? cms['350615'] : cms['350614']}
                        onClick={onClickShowAll}
                    />
                )}
            </Box>
        </>
    );
};

SubcategoryFilterItems.displayName = 'SubcategoryFilterItem';

export default SubcategoryFilterItems;
